import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import TabPageLayout, { PageLoader, EmptyState, DataCard, StatCard } from './TabPageLayout';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ServiceToggle from './ServiceToggle';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import { 
  Plus, 
  Grid3x3, 
  Link as LinkIcon, 
  Copy, 
  QrCode, 
  Pencil, 
  Trash2, 
  Eye, 
  ExternalLink,
  Check,
  Sparkles,
  TrendingUp,
  Users,
  ChevronUp,
  ChevronDown,
  X,
  Star,
  Type
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { BACKEND_URL } from '../config/api';
import apiV2 from '../utils/apiV2';

// Code display options (not saved to backend, just for UI)
const CODE_OPTIONS = [
  {
    id: 'embed',
    name: 'Embed Code',
    icon: Grid3x3,
  },
  {
    id: 'url',
    name: 'Direct URL',
    icon: LinkIcon,
  }
];

const MiniFlowsTab = ({ onNavigate }) => {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const organizationId = userData.organizationId || localStorage.getItem('organizationId');
  const orgSlug = localStorage.getItem('organizationSlug');
  
  // State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [services, setServices] = useState([]);
  const [previewFlow, setPreviewFlow] = useState(null); // Flow being previewed
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [codeViewMode, setCodeViewMode] = useState({}); // Track embed vs url per flow
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    widget_type: 'service-first',
    service_ids: [],
    step_config: {
      step_order: ['guests', 'duration', 'addons', 'questions'],
      skip_steps: []
    },
    branding: {
      heading: '',
      subheading: '',
      button_text: 'Book Now',
      show_logo: false,
      show_company_name: true,
      show_google_reviews: false
    },
    is_active: true
  });
  
  const baseUrl = window.location.origin;
  
  // Fetch org data to get logo URL for the "Show Logo" toggle
  const { data: orgData } = useSWR(
    '/auth/organizations/me',
    (url) => apiV2.get(url).then(r => r.data),
    { revalidateOnFocus: false }
  );
  const orgLogoUrl = orgData?.logoUrl || orgData?.logo_url || null;
  
  // Fetch flows
  const { data: flowsData, error: flowsError, isLoading, mutate: refreshFlows } = useSWR(
    organizationId ? `${BACKEND_URL}/api/flows/org/${organizationId}?include_inactive=true` : null,
    async (url) => {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch flows');
      return response.json();
    },
    { revalidateOnFocus: true }
  );
  
  // Fetch services for the selector
  useEffect(() => {
    const fetchServices = async () => {
      if (!organizationId) return;
      try {
        const response = await apiV2.get(`/organizations/${organizationId}/services`);
        setServices(response.data?.services || []);
      } catch (error) {
        console.error('Failed to fetch services:', error);
      }
    };
    fetchServices();
  }, [organizationId]);
  
  const flows = flowsData?.flows || [];
  
  // Calculate total stats
  const totalViews = flows.reduce((sum, f) => sum + (f.total_views || 0), 0);
  const totalStarts = flows.reduce((sum, f) => sum + (f.total_starts || 0), 0);
  const totalCompletions = flows.reduce((sum, f) => sum + (f.total_completions || 0), 0);
  const overallConversion = totalViews > 0 ? ((totalCompletions / totalViews) * 100).toFixed(1) : 0;
  
  // Create flow
  const handleCreateFlow = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a flow name');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/flows/org/${organizationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to create flow');
      
      toast.success(`Flow "${formData.name}" created!`);
      setShowCreateModal(false);
      resetForm();
      refreshFlows();
    } catch (error) {
      console.error('Create flow error:', error);
      toast.error('Failed to create flow');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Update flow
  const handleUpdateFlow = async () => {
    if (!selectedFlow) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/flows/org/${organizationId}/${selectedFlow.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to update flow');
      
      toast.success('Flow updated!');
      setShowEditModal(false);
      setSelectedFlow(null);
      resetForm();
      refreshFlows();
    } catch (error) {
      console.error('Update flow error:', error);
      toast.error('Failed to update flow');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Delete flow - show confirmation modal
  const handleDeleteFlow = (flowId, flowName) => {
    setFlowToDelete({ id: flowId, name: flowName });
    setShowDeleteModal(true);
  };
  
  // Confirm delete
  const confirmDelete = async () => {
    if (!flowToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/flows/org/${organizationId}/${flowToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete flow');
      
      toast.success('Flow deleted');
      refreshFlows();
    } catch (error) {
      console.error('Delete flow error:', error);
      toast.error('Failed to delete flow');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setFlowToDelete(null);
    }
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      widget_type: 'service-first',
      service_ids: [],
      step_config: {
        step_order: ['guests', 'duration', 'addons', 'questions'],
        skip_steps: []
      },
      branding: {
        heading: '',
        subheading: '',
        button_text: 'Book Now',
        show_logo: false
      },
      is_active: true
    });
  };
  
  // Open edit modal with flow data
  const openEditModal = (flow) => {
    setSelectedFlow(flow);
    setFormData({
      name: flow.name,
      description: flow.description || '',
      widget_type: flow.widget_type,
      service_ids: flow.service_ids || [],
      step_config: flow.step_config || {
        step_order: ['guests', 'duration', 'addons', 'questions'],
        skip_steps: []
      },
      branding: flow.branding || {
        heading: '',
        subheading: '',
        button_text: 'Book Now',
        show_logo: false,
        show_company_name: true,
        show_google_reviews: false
      },
      is_active: flow.is_active
    });
    setShowEditModal(true);
  };
  
  // Copy to clipboard
  const copyToClipboard = (text, message = 'Copied!') => {
    navigator.clipboard.writeText(text);
    toast.success(message);
  };
  
  // Get preview URL for a flow
  const getPreviewUrl = (flow) => {
    const flowParam = flow.slug ? `&flow=${flow.slug}` : '';
    return `${baseUrl}/services-grid.html?org=${orgSlug}${flowParam}&preview=true`;
  };
  
  // Open preview modal
  const openPreview = (flow) => {
    setPreviewFlow(flow);
    setShowPreviewModal(true);
  };
  
  // Show QR code
  const showQR = (flow) => {
    const url = `${baseUrl}/services-grid.html?org=${orgSlug}&flow=${flow.slug}`;
    setQrUrl(url);
    setShowQRModal(true);
  };
  
  // Toggle service selection
  const toggleService = (serviceId) => {
    const current = formData.service_ids || [];
    const isSelected = current.includes(serviceId);
    
    setFormData({
      ...formData,
      service_ids: isSelected
        ? current.filter(s => s !== serviceId)
        : [...current, serviceId]
    });
  };
  
  if (isLoading) {
    return (
      <TabPageLayout title="Booking Flows" subtitle="Embed booking flows on your website to let customers book directly">
        <PageLoader />
      </TabPageLayout>
    );
  }
  
  return (
    <TabPageLayout 
      title="Booking Flows"
      subtitle="Embed booking flows on your website or let your customers book directly"
      actions={
        <Button onClick={() => setShowCreateModal(true)} className="gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4" />
          New Flow
        </Button>
      }
    >
      <div className="space-y-6">
        {flows.length === 0 ? (
          <EmptyState
            icon={<Sparkles className="w-10 h-10" />}
            title="No flows yet"
            description="Create your first booking flow to filter services for different campaigns"
            action={
              <Button onClick={() => setShowCreateModal(true)} className="gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4" />
                Create Your First Flow
              </Button>
            }
          />
        ) : (
          <div className="space-y-6">
            {flows.map((flow) => {
              // Use current frontend URL for embed codes
              // The frontend proxies /api/* requests to the backend, so data-api should be the frontend URL
              // Direct link URL for sharing - points to services-grid.html with flow param
              const embedUrl = `${baseUrl}/services-grid.html?org=${orgSlug}&flow=${flow.slug}`;
              // Clean script embed - one div + one script (both src and data-api use frontend URL)
              const embedCode = `<div id="bf-widget"></div>
<script src="${baseUrl}/bf-widget.js" data-org="${orgSlug}" data-flow="${flow.slug}" data-api="${baseUrl}"></script>`;
              const isExpanded = previewFlow?.id === flow.id;
              
              // Calculate conversion rate for this flow
              const flowViews = flow.total_views || 0;
              const flowStarts = flow.total_starts || 0;
              const flowCompletions = flow.total_completions || 0;
              const flowConversion = flowViews > 0 ? ((flowCompletions / flowViews) * 100).toFixed(1) : 0;
              
              return (
                <div key={flow.id} className="bg-white rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
                  {/* Flow Header */}
                  <div className="p-4 sm:p-6 pb-3 sm:pb-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <Grid3x3 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">{flow.name}</h3>
                          <p className="text-xs text-gray-500">
                            {flow.service_ids?.length ? `${flow.service_ids.length} service${flow.service_ids.length !== 1 ? 's' : ''}` : 'All services'}
                          </p>
                        </div>
                      </div>
                      {/* Active badge — top right */}
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        {flow.is_active ? (
                          <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">Active</span>
                        ) : (
                          <span className="text-xs text-gray-400 font-medium bg-gray-100 px-2 py-0.5 rounded-full">Inactive</span>
                        )}
                      </div>
                    </div>

                    {/* Stats row — always visible, compact */}
                    <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm mb-3">
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-gray-900">{flowViews}</span>
                        <span className="text-gray-400">views</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-gray-900">{flowCompletions}</span>
                        <span className="text-gray-400">bookings</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-blue-600">{flowConversion}%</span>
                        <span className="text-gray-400">conv</span>
                      </div>
                    </div>
                    {/* Code Type Toggle */}
                    <div className="flex gap-2 flex-wrap mb-3">
                      {CODE_OPTIONS.map((option) => {
                        const Icon = option.icon;
                        const currentMode = codeViewMode[flow.id] || 'embed';
                        const isSelected = currentMode === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => setCodeViewMode({ ...codeViewMode, [flow.id]: option.id })}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                              isSelected 
                                ? 'border-blue-600 bg-blue-50 text-blue-700' 
                                : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <Icon className="w-3 h-3" />
                            {option.name}
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Code Block */}
                    <div className="relative">
                      <div className="bg-gray-900 rounded-xl p-4 pr-10 overflow-x-auto">
                        {(codeViewMode[flow.id] || 'embed') === 'embed' ? (
                          <code className="text-sm font-mono whitespace-nowrap">
                            <span className="text-gray-500">&lt;</span>
                            <span className="text-pink-400">div</span>
                            <span className="text-gray-500"> </span>
                            <span className="text-green-400">id</span>
                            <span className="text-gray-500">=</span>
                            <span className="text-blue-400">&quot;bf-widget&quot;</span>
                            <span className="text-gray-500">&gt;&lt;/</span>
                            <span className="text-pink-400">div</span>
                            <span className="text-gray-500">&gt;&lt;</span>
                            <span className="text-pink-400">script</span>
                            <span className="text-gray-500"> </span>
                            <span className="text-green-400">src</span>
                            <span className="text-gray-500">=</span>
                            <span className="text-blue-400">&quot;{baseUrl}/bf-widget.js&quot;</span>
                            <span className="text-gray-500"> </span>
                            <span className="text-green-400">data-org</span>
                            <span className="text-gray-500">=</span>
                            <span className="text-blue-400">&quot;{orgSlug}&quot;</span>
                            <span className="text-gray-500"> </span>
                            <span className="text-green-400">data-flow</span>
                            <span className="text-gray-500">=</span>
                            <span className="text-blue-400">&quot;{flow.slug}&quot;</span>
                            <span className="text-gray-500">&gt;&lt;/</span>
                            <span className="text-pink-400">script</span>
                            <span className="text-gray-500">&gt;</span>
                          </code>
                        ) : (
                          <code className="text-sm font-mono whitespace-nowrap text-blue-400">
                            {embedUrl}
                          </code>
                        )}
                      </div>
                      {/* Copy button — centered vertically */}
                      <button
                        onClick={() => copyToClipboard(
                          (codeViewMode[flow.id] || 'embed') === 'embed' ? embedCode : embedUrl,
                          'Copied!'
                        )}
                        className="absolute right-2 top-0 bottom-0 m-auto w-7 h-7 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </div>

                    {/* Helper text */}
                    <p className="text-xs text-gray-500 mt-2">
                      {(codeViewMode[flow.id] || 'embed') === 'embed'
                        ? 'Paste into your website.'
                        : 'Share this link anywhere.'
                      }{' '}
                      <button onClick={() => openEditModal(flow)} className="text-blue-600 hover:text-blue-700 font-medium">
                        Toggle status
                      </button>
                    </p>

                    {/* Action row — compact icons */}
                    <div className="flex items-center gap-1 mt-3">
                      <button
                        onClick={() => setPreviewFlow(isExpanded ? null : flow)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Preview
                      </button>
                      <button
                        onClick={() => showQR(flow)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                      >
                        <QrCode className="w-3.5 h-3.5" />
                        QR
                      </button>
                      <div className="flex-1" />
                      <button
                        onClick={() => openEditModal(flow)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteFlow(flow.id, flow.name)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Expandable Preview */}
                  {isExpanded && (
                    <div className="bg-gray-50 p-4">
                      <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                        <iframe
                          src={`${baseUrl}/services-grid.html?org=${orgSlug}&flow=${flow.slug}&embed=true&preview=true`}
                          className="w-full border-none"
                          style={{ height: '600px' }}
                          title={`Preview ${flow.name}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          )}
      </div>
      
      {/* Create/Edit Flow Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={() => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setSelectedFlow(null);
        resetForm();
      }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedFlow ? 'Edit Flow' : 'Create New Flow'}</DialogTitle>
            <DialogDescription>
              {selectedFlow 
                ? 'Update your booking flow settings' 
                : 'Set up a new booking experience for your customers'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Basic Info - Just name */}
            <div>
              <label className="text-sm font-medium text-gray-700">Flow Name <span className="text-red-500">*</span></label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Summer Sale 2025, Facebook Ad Campaign"
                className="mt-1.5"
              />
            </div>
            
            {/* Unified Services List - Toggle on/off and reorder */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Services
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Toggle services on/off. Selected services appear in order — use arrows to reorder.
              </p>
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                {services.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">No services found</p>
                ) : (
                  <>
                    {/* Selected services - shown in their selected order */}
                    {(formData.service_ids || []).map((serviceId, index) => {
                      const service = services.find(s => s.id === serviceId);
                      if (!service) return null;
                      const isServiceOff = service.active === false;
                      
                      return (
                        <div
                          key={service.id}
                          className={`flex items-center gap-3 p-3 border-b border-gray-100 ${isServiceOff ? 'bg-gray-50' : 'bg-blue-50/50'}`}
                        >
                          {/* Toggle Switch */}
                          <ServiceToggle
                            checked={true}
                            onChange={() => toggleService(service.id)}
                          />
                          
                          {/* Position badge */}
                          <span className={`w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 ${isServiceOff ? 'bg-gray-400' : 'bg-blue-600'}`}>
                            {index + 1}
                          </span>
                          
                          {/* Service name — truncated */}
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium truncate block ${isServiceOff ? 'text-gray-400' : 'text-gray-900'}`}>
                              {service.name}
                            </span>
                            {isServiceOff && (
                              <span className="text-xs text-amber-600">Off</span>
                            )}
                          </div>
                          
                          {/* Reorder controls — centered arrows */}
                          {(formData.service_ids || []).length > 1 && (
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              <button
                                onClick={() => {
                                  if (index === 0) return;
                                  const newIds = [...formData.service_ids];
                                  [newIds[index - 1], newIds[index]] = [newIds[index], newIds[index - 1]];
                                  setFormData({ ...formData, service_ids: newIds });
                                }}
                                disabled={index === 0}
                                className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                                title="Move up"
                              >
                                <ChevronUp className="w-4 h-4 text-gray-500" />
                              </button>
                              <button
                                onClick={() => {
                                  if (index === formData.service_ids.length - 1) return;
                                  const newIds = [...formData.service_ids];
                                  [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
                                  setFormData({ ...formData, service_ids: newIds });
                                }}
                                disabled={index === formData.service_ids.length - 1}
                                className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                                title="Move down"
                              >
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Divider if there are both selected and unselected */}
                    {(formData.service_ids || []).length > 0 && 
                     services.filter(s => !(formData.service_ids || []).includes(s.id)).length > 0 && (
                      <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 font-medium">
                        Available Services
                      </div>
                    )}
                    
                    {/* Unselected services */}
                    {services.filter(s => !(formData.service_ids || []).includes(s.id)).map((service) => {
                      const isServiceOff = service.active === false;
                      
                      return (
                        <div
                          key={service.id}
                          className={`flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 ${isServiceOff ? 'bg-gray-50' : 'bg-white'}`}
                        >
                          {/* Toggle Switch */}
                          <ServiceToggle
                            checked={false}
                            onChange={() => toggleService(service.id)}
                          />
                          
                          {/* Service name */}
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm ${isServiceOff ? 'text-gray-400' : 'text-gray-600'}`}>
                              {service.name}
                            </span>
                            {isServiceOff && (
                              <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                Service Off
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              {(formData.service_ids || []).length === 0 && services.length > 0 && (
                <p className="text-xs text-gray-500 mt-2 italic">
                  No services selected — all services will be shown by default.
                </p>
              )}
            </div>
            
            {/* Show Logo Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3 min-w-0">
                {orgLogoUrl ? (
                  <img
                    src={orgLogoUrl}
                    alt="Logo"
                    className="h-8 w-auto max-w-[64px] object-contain rounded-md flex-shrink-0"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-md bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-gray-400 text-xs">No logo</span>
                  </div>
                )}
                <div>
                  <p className="font-medium text-sm">Show Logo</p>
                  <p className="text-xs text-gray-500">
                    {orgLogoUrl
                      ? 'Display your logo at the top of this flow'
                      : 'Upload a logo in the Branding tab first'}
                  </p>
                </div>
              </div>
              <ServiceToggle
                checked={!!formData.branding?.show_logo}
                onChange={(checked) =>
                  setFormData({ ...formData, branding: { ...formData.branding, show_logo: checked } })
                }
              />
            </div>
            
            {/* Show Company Name Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Type className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">Show Company Name</p>
                  <p className="text-xs text-gray-500">Display your company name as the heading</p>
                </div>
              </div>
              <ServiceToggle
                checked={formData.branding?.show_company_name !== false}
                onChange={(checked) =>
                  setFormData({ ...formData, branding: { ...formData.branding, show_company_name: checked } })
                }
              />
            </div>
            
            {/* Show Google Reviews Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                </div>
                <div>
                  <p className="font-medium text-sm">Show Google Reviews</p>
                  <p className="text-xs text-gray-500">Display 5-star badge with review count</p>
                </div>
              </div>
              <ServiceToggle
                checked={!!formData.branding?.show_google_reviews}
                onChange={(checked) =>
                  setFormData({ ...formData, branding: { ...formData.branding, show_google_reviews: checked } })
                }
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-sm">Flow Status</p>
                <p className="text-xs text-gray-500">
                  {formData.is_active ? 'Live and accepting bookings' : 'Hidden from customers'}
                </p>
              </div>
              <ServiceToggle
                checked={formData.is_active}
                onChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              className="h-10"
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedFlow(null);
                resetForm();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={selectedFlow ? handleUpdateFlow : handleCreateFlow}
              disabled={!formData.name || isSubmitting}
              className="bg-blue-600 hover:bg-blue-700 text-white h-10"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  {selectedFlow ? 'Saving...' : 'Creating...'}
                </span>
              ) : (
                selectedFlow ? 'Save Changes' : 'Create Flow'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* QR Code Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>QR Code</DialogTitle>
            <DialogDescription>Scan to open booking page</DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center py-4">
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <QRCodeCanvas value={qrUrl} size={180} />
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center break-all px-2">{qrUrl}</p>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => copyToClipboard(qrUrl, 'URL copied!')}
              className="flex-1 h-9"
            >
              <Copy className="w-4 h-4 mr-1.5" />
              Copy URL
            </Button>
            <Button 
              onClick={() => {
                const canvas = document.querySelector('canvas');
                if (canvas) {
                  const url = canvas.toDataURL('image/png');
                  const link = document.createElement('a');
                  link.download = 'booking-qr-code.png';
                  link.href = url;
                  link.click();
                  toast.success('QR code downloaded!');
                }
              }}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-9"
            >
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Preview Modal - Full Screen */}
      {showPreviewModal && previewFlow && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-blue-100 text-blue-600">
                  <Grid3x3 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {previewFlow.name || 'Booking Widget'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Browse services → Pick date → Book
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    const flowParam = previewFlow.slug ? `&flow=${previewFlow.slug}` : '';
                    const url = `${baseUrl}/services-grid.html?org=${orgSlug}${flowParam}`;
                    copyToClipboard(url, 'URL copied!');
                  }}
                  className="gap-1.5 h-9"
                >
                  <Copy className="w-4 h-4" />
                  Copy URL
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const flowParam = previewFlow.slug ? `&flow=${previewFlow.slug}` : '';
                    const url = `${baseUrl}/services-grid.html?org=${orgSlug}${flowParam}`;
                    window.open(url, '_blank');
                  }}
                  className="gap-1.5 h-9"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open
                </Button>
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    setPreviewFlow(null);
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            {/* Preview Frame */}
            <div className="flex-1 overflow-hidden bg-gray-100">
              <iframe
                src={`${baseUrl}/services-grid.html?org=${orgSlug}${previewFlow.slug ? `&flow=${previewFlow.slug}` : ''}&embed=true&preview=true`}
                className="w-full h-full border-none"
                title="Flow Preview"
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Flow</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{flowToDelete?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteModal(false)}
              className="h-10"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              className="h-10"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Deleting...
                </span>
              ) : (
                'Delete Flow'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default MiniFlowsTab;
